import * as React from "react"
import { OctagonAlert } from "lucide-react"

import { cn } from "@/lib/utils"

interface InputProps extends React.ComponentProps<"input"> {
  error?: boolean | string;        // Si hay error (boolean o mensaje)
  showError?: boolean;              // Controla si se muestra el error
  errorMessage?: string;            // Mensaje de error personalizado
  variant?: 'default' | 'error' | 'success';  // Variante visual
  helperText?: string;              // Texto de ayuda debajo del input
  label?: string;                   // Label opcional
  autoScrollOnError?: boolean;      // Auto scroll cuando hay error
}

const Input = React.forwardRef<HTMLInputElement, InputProps>(
  (
    {
      error,
      showError = true,
      errorMessage,
      variant = 'default',
      helperText,
      label,
      autoScrollOnError = true,
      className,
      type,
      ...props
    },
    ref
  ) => {
    // Determinar si hay error visible
    const hasError = error && showError;

    // Ref interno para manejar el scroll
    const internalRef = React.useRef<HTMLInputElement>(null);
    const inputRef = (ref as React.RefObject<HTMLInputElement>) || internalRef;

    // Auto scroll al input cuando aparece un error
    React.useEffect(() => {
      if (hasError && autoScrollOnError && inputRef.current) {
        // Pequeño delay para asegurar que el DOM se haya actualizado
        setTimeout(() => {
          if (inputRef.current) {
            inputRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
            inputRef.current.focus();
          }
        }, 100);
      }
    }, [hasError, autoScrollOnError]);

    // Clases de borde según estado
    const getBorderColor = () => {
      if (hasError) return 'border-red-500 focus-visible:ring-red-500';
      if (variant === 'success') return 'border-green-500 focus-visible:ring-green-500';
      return 'border-input focus-visible:ring-ring';
    };

    // Mensaje a mostrar (prioridad: errorMessage > error si es string)
    const displayMessage = errorMessage || (typeof error === 'string' ? error : '');

    return (
      <div className="w-full">
        {/* Label opcional */}
        {label && (
          <label className="block text-sm font-medium text-back mb-1.5 font-montserrat">
            {label}
          </label>
        )}

        {/* Input */}
        <input
          type={type}
          ref={inputRef as React.RefObject<HTMLInputElement>}
          className={cn(
            "flex h-10 w-full rounded-[5px] border bg-background px-3 py-1 text-base ring-offset-background file:border-1 file:bg-transparent file:text-sm file:font-medium file:text-foreground placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-offset-0 disabled:cursor-not-allowed disabled:opacity-50 md:text-sm transition-colors font-montserrat",
            getBorderColor(),
            className
          )}
          {...props}
        />

        {/* Mensaje de error */}
        {hasError && displayMessage && (
          <p className="text-red-500 text-[12px] flex items-center gap-1.5 font-poppins">
            <OctagonAlert className="h-4 w-4 flex-shrink-0" strokeWidth={2.5} />
            <span className="text-[12px] mt-1 font-poppins">{displayMessage}</span>
          </p>
        )}

        {/* Texto de ayuda (solo si no hay error) */}
        {!hasError && helperText && (
          <p className="text-gray-500 text-[12px] mt-1 font-montserrat">{helperText}</p>
        )}
      </div>
    );
  }
);

Input.displayName = "Input"

export { Input }
